/**
 * Statement import.
 *
 * Maybank2u hands out monthly statements as PDF rather than CSV, so this reads
 * the text of one and shows every row it found in a table you tick through
 * before anything is saved.
 *
 * The preview is not a formality. The parser has never seen a real statement --
 * only invented ones written to plausible shapes -- so the table is how a
 * layout it misread becomes visible instead of silently wrong. Where the
 * statement has a running balance, the arithmetic check above the table is a
 * genuine proof that the right column was read.
 */
import { useRef, useState } from 'react';
import {
  checkRunningBalance,
  formatMoney,
  parseStatementText,
  reconcileWithBalance,
  statementToTransactions,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { saveTransactions } from '../db/repository.js';
import { Button, Field, Notice, Select, TextArea } from '../components/primitives.js';
import { extractTextFromFile, type ExtractionProgress } from '../import/extract.js';

type Parsed = ReturnType<typeof parseStatementText>;

export function StatementImport(): JSX.Element {
  const { accounts, categories, rules, merchantHistory, settings, toast } = useApp();
  const [text, setText] = useState('');
  const [account, setAccount] = useState('');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | undefined>();
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });
  const fileInput = useRef<HTMLInputElement>(null);

  function handleParse(source = text): void {
    setError(undefined);
    setExcluded(new Set());

    const result = parseStatementText(source, { timeZone: settings.timeZone });
    if (result.rows.length === 0) {
      setParsed(null);
      setError(result.warnings[0] ?? 'No transaction rows were found in that statement.');
      return;
    }
    // The running balance is arithmetic, so it outranks any heuristic.
    setParsed({ ...result, rows: reconcileWithBalance(result.rows) });
  }

  async function handleFile(file: File): Promise<void> {
    setError(undefined);
    setParsed(null);
    cancelRef.current = { cancelled: false };

    try {
      const extracted = await extractTextFromFile(file, {
        onProgress: setProgress,
        signal: cancelRef.current,
      });
      setProgress(null);
      if (!extracted.text.trim()) {
        setError(extracted.reason ?? 'No text could be read from that file.');
        return;
      }
      setText(extracted.text);
      handleParse(extracted.text);
    } catch (failure) {
      setProgress(null);
      setError(failure instanceof Error ? failure.message : 'That file could not be read.');
    }
  }

  const included = parsed ? parsed.rows.filter((row) => !excluded.has(row.index)) : [];
  const balance = parsed ? checkRunningBalance(parsed.rows) : null;
  const total = included
    .filter((row) => row.type === 'expense')
    .reduce((sum, row) => sum + row.amountSen, 0);

  async function handleImport(): Promise<void> {
    if (included.length === 0) return;

    const imported = statementToTransactions(included, {
      accountLabel: account || settings.defaultAccountLabel,
      categories,
      rules,
      history: merchantHistory.map((entry) => ({
        merchant: entry.merchant,
        categoryId: entry.categoryId,
        count: entry.count,
      })),
      defaultCategoryId: settings.defaultCategoryId,
    });

    const count = await saveTransactions(imported.map((entry) => entry.transaction));
    toast(`Imported ${count} ${count === 1 ? 'payment' : 'payments'}`);
    setParsed(null);
    setText('');
  }

  return (
    <section className="stack">
      <Notice tone="info">
        For a monthly statement rather than a single receipt. Pick the PDF, or paste the text.
        Everything is read on this device, and nothing is saved until you press Import.
      </Notice>

      <Field label="Paid with" hint="Which account this statement belongs to.">
        {(props) => (
          <Select {...props} value={account} onChange={(event) => setAccount(event.target.value)}>
            <option value="">{settings.defaultAccountLabel} (default)</option>
            {accounts
              .filter((entry) => entry.label !== settings.defaultAccountLabel)
              .map((entry) => (
                <option key={entry.id} value={entry.label}>
                  {entry.label}
                </option>
              ))}
          </Select>
        )}
      </Field>

      <div className="row">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,text/plain,.pdf,.txt"
          className="visually-hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
            event.target.value = '';
          }}
        />
        <Button className="grow" onClick={() => fileInput.current?.click()}>
          Choose a PDF
        </Button>
        <Button
          variant="primary"
          className="grow"
          disabled={!text.trim()}
          onClick={() => handleParse()}
        >
          Read statement
        </Button>
      </div>

      <Field label="Or paste the statement text" hint="Useful when the PDF turns out to be a scan.">
        {(props) => (
          <TextArea
            {...props}
            rows={5}
            value={text}
            placeholder="02/05/2024  DUITNOW QR WARUNG CONTOH   12.00-   988.00"
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>

      {progress ? (
        <div className="card">
          <p className="small">{progress.message}</p>
          <div className="budget__track" style={{ marginTop: 'var(--space-2)' }}>
            <div
              className="budget__fill"
              style={{ width: `${Math.round(progress.ratio * 100)}%` }}
            />
          </div>
          <Button
            variant="quiet"
            block
            style={{ marginTop: 'var(--space-3)' }}
            onClick={() => {
              cancelRef.current.cancelled = true;
              setProgress(null);
            }}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {parsed ? (
        <div className="card stack">
          <h3 className="section__title">{parsed.rows.length} rows found</h3>

          {balance?.consistent ? (
            <Notice tone="info">
              The running balance adds up across all {balance.checked} checked rows, so the amounts
              and directions below are arithmetically confirmed.
            </Notice>
          ) : null}

          {balance && balance.checked > 0 && !balance.consistent ? (
            <Notice tone="warning">
              {balance.mismatches.length} row
              {balance.mismatches.length === 1 ? ' does' : 's do'} not match the running balance.
              Check those amounts closely before importing.
            </Notice>
          ) : null}

          {parsed.warnings.map((warning) => (
            <Notice tone="warning" key={warning}>
              {warning}
            </Notice>
          ))}

          <p className="small muted">
            Untick anything you do not want. {included.length} of {parsed.rows.length} selected,{' '}
            {formatMoney(total)} of spending.
          </p>

          <div className="scroll-x">
            <table className="data-table">
              <caption className="visually-hidden">Rows found in the statement</caption>
              <thead>
                <tr>
                  <th scope="col">Use</th>
                  <th scope="col">Date</th>
                  <th scope="col">Description</th>
                  <th scope="col">Type</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((row) => (
                  <tr key={row.index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!excluded.has(row.index)}
                        aria-label={`Import ${row.description}`}
                        style={{ width: 22, height: 22 }}
                        onChange={(event) =>
                          setExcluded((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.delete(row.index);
                            else next.add(row.index);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td>{row.occurredAt.slice(0, 10)}</td>
                    <td>{row.description}</td>
                    <td>
                      {row.type}
                      {row.confidence < 0.8 ? (
                        <span className="badge badge--review" style={{ marginLeft: 4 }}>
                          check
                        </span>
                      ) : null}
                    </td>
                    <td className="money">{formatMoney(row.amountSen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsed.skipped.length > 0 ? (
            <details>
              <summary className="small">{parsed.skipped.length} lines skipped</summary>
              <ul className="stack" style={{ marginTop: 'var(--space-2)' }}>
                {parsed.skipped.slice(0, 20).map((entry) => (
                  <li key={entry.line} className="tiny faint">
                    {entry.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <Button variant="accent" block disabled={included.length === 0} onClick={handleImport}>
            Import {included.length} {included.length === 1 ? 'payment' : 'payments'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
